// packages/renderer/src/plugins/commandlet-run.ts
//
// Runs what `commandlet-lang.ts` parsed. Every effect here already exists
// somewhere in the app; this module only routes to it:
//   - filters and sort are a patch of `Table.filters` / `sortBy`, which
//     `data-table.applyTable()` re-reads on its own subscription,
//   - windows come from `focusTableWindow` / `revealViewWindow`,
//   - search rides the `easydb:table-search` / `easydb:set-search` events,
//   - `cmd/` looks up the id in `registries.commands`.

import type { ColumnSpec, CommandSpec, Row, SortSpec, Table, ViewInstance } from '@easydb/shared';
import { getContext } from '../app-context.js';
import { focusTableWindow } from '../window-mgr/table-window-manager.js';
import { revealViewWindow } from '../window-mgr/view-window-manager.js';
import { CommandletError, parseCommandlets, substituteCommandlet, type Commandlet } from './commandlet-lang.js';
import { planEditKey } from './commandlet-edit.js';
import { keyColumnOf, namesAField, planPreview } from './commandlet-preview.js';
import { openHtmlEditor } from './html-cell-editor.js';
import { openPreviewPopup, previewFrame, renderValue } from './preview-popup.js';
import { validateValue } from '../table/validate-value.js';
import { readRows } from '../db/row-reader.js';
import { evaluateRow } from '../views/view-render.js';

/**
 * Where a commandlet was invoked from, when that is known. A link clicked inside
 * a cell knows its table, column and value; the same string typed into the
 * palette or arriving in a `#hash` knows none of them.
 */
export interface CommandletContext {
  tableId?: string | undefined;
  /** The view the click came from — what a target-less `view?…` acts on. */
  viewInstanceId?: string | undefined;
  field?: string | undefined;
  value?: string | undefined;
  /** Extra placeholders, e.g. `HASH` and `1`…`9` from an anchor rule. */
  vars?: Record<string, string> | undefined;
}

/** Parse and run a `;`-separated commandlet string, left to right. */
export async function runCommandletString(input: string, ctx: CommandletContext = {}): Promise<void> {
  const commandlets = parseCommandlets(input);
  const vars = await placeholders(ctx);
  for (const parsed of commandlets) {
    await runOne(substituteCommandlet(parsed, vars), ctx);
  }
}

/**
 * Is this string a commandlet this workspace can actually run? Same parse and
 * the same lookups as running it — the table, the columns, the view, the
 * command id — but nothing is written.
 *
 * Exists so the dialog can say whether what is being typed will work BEFORE it
 * is run, instead of the user finding out from an error toast afterwards.
 */
export async function checkCommandletString(input: string, ctx: CommandletContext = {}): Promise<{ ok: boolean; message: string }> {
  const text = input.trim();
  if (!text) return { ok: false, message: '' };
  let parsed: Commandlet[];
  try {
    parsed = parseCommandlets(text);
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : String(err) };
  }
  const vars = await placeholders(ctx);
  const summaries: string[] = [];
  for (const cmd of parsed) {
    try {
      summaries.push(await describe(substituteCommandlet(cmd, vars), ctx));
    } catch (err) {
      return { ok: false, message: err instanceof Error ? err.message : String(err) };
    }
  }
  return { ok: true, message: summaries.join(', then ') };
}

/** One line saying what a commandlet WOULD do, throwing if it could not. */
async function describe(cmd: Commandlet, ctx: CommandletContext = {}): Promise<string> {
  switch (cmd.verb) {
    case 'goto': {
      const table = await resolveTable(cmd, ctx);
      const fields = Object.keys(cmd.filters).map((k) => resolveField(table, k));
      const sort = parseSort(cmd.options.sort, table);
      const parts = [`open ${table.name}`];
      if (fields.length > 0) parts.push(`filter ${fields.join(' + ')}`);
      if (cmd.options.clear !== undefined) parts.push('clear filters');
      if (cmd.options.search !== undefined) parts.push(`search "${cmd.options.search}"`);
      if (sort && sort.length > 0) parts.push(`sort by ${sort.map((s) => `${s.field}${s.asc ? '' : ' ↓'}`).join(', ')}`);
      return parts.join(', ');
    }
    case 'search':
      return `search all tables for "${cmd.targets[0] ?? ''}"`;
    case 'view': {
      const inst = await resolveView(cmd, ctx);
      const table = await tableOfView(inst);
      const fields = Object.keys(cmd.filters).map((k) => resolveField(table, k));
      const sort = parseSort(cmd.options.sort, table);
      const parts = [`open view "${inst.name}"`];
      if (fields.length > 0) parts.push(`filter ${fields.join(' + ')}`);
      if (cmd.options.clear !== undefined) parts.push('clear filters');
      if (cmd.options.search !== undefined) parts.push(`search "${cmd.options.search}"`);
      if (sort && sort.length > 0) parts.push(`sort by ${sort.map((s) => `${s.field}${s.asc ? '' : ' ↓'}`).join(', ')}`);
      return parts.join(', ');
    }
    case 'cmd':
      return `run "${(await findCommandById(cmd.targets[0] ?? '')).title}"`;
    case 'preview': {
      const table = await resolveTable(cmd, ctx);
      const plan = planPreview(table, cmd.targets.slice(1));
      if ('error' in plan) throw new CommandletError(plan.error);
      const fields = Object.keys(cmd.filters).map((k) => resolveField(table, k));
      const where = [...Object.keys(plan.keyFilter), ...fields];
      return `preview ${table.name}.${plan.field.label || plan.field.field}${where.length > 0 ? ` where ${where.join(' + ')}` : ''}`;
    }
    case 'edit': {
      const table = await resolveTable(cmd, ctx);
      return `edit the ${table.name} record where ${describeFilters(editFilters(table, cmd))}`;
    }
    case 'record': {
      const table = await resolveTable(cmd, ctx);
      const byId = rowIdTarget(cmd);
      return byId ? `open the ${table.name} record ${byId}` : `open the ${table.name} record where ${describeFilters(editFilters(table, cmd))}`;
    }
    case 'ui':
      throw new CommandletError(`"${cmd.verb}" is not wired up yet.`);
  }
}

/** `$TABLE`, `$FIELD`, `$VALUE`, `$WORKSPACE` plus whatever the caller supplied. */
async function placeholders(ctx: CommandletContext): Promise<Record<string, string>> {
  const app = await getContext();
  const vars: Record<string, string> = { WORKSPACE: app.workspaceId, ...(ctx.vars ?? {}) };
  if (ctx.field !== undefined) vars.FIELD = ctx.field;
  if (ctx.value !== undefined) vars.VALUE = ctx.value;
  if (ctx.tableId) {
    const table = await app.store.tables.findOne(ctx.tableId);
    if (table) vars.TABLE = table.name;
  }
  return vars;
}

async function runOne(cmd: Commandlet, ctx: CommandletContext = {}): Promise<void> {
  switch (cmd.verb) {
    case 'goto':
      return runGoto(cmd, ctx);
    case 'search':
      return runSearch(cmd);
    case 'view':
      return runView(cmd, ctx);
    case 'cmd':
      return runCommandId(cmd);
    case 'preview':
      return runPreview(cmd, ctx);
    case 'edit':
      return runEdit(cmd, ctx);
    case 'record':
      return runRecord(cmd, ctx);
    case 'ui':
      throw new CommandletError(`"${cmd.verb}" is not wired up yet.`);
  }
}

// -- goto ---------------------------------------------------------------------

async function runGoto(cmd: Commandlet, ctx: CommandletContext): Promise<void> {
  const table = await resolveTable(cmd, ctx);

  const patch: Partial<Table> = {};
  const merged = cmd.options.clear === undefined ? { ...(table.filters ?? {}) } : {};
  for (const [key, expr] of Object.entries(cmd.filters)) {
    const field = resolveField(table, key);
    // An empty value is how a commandlet REMOVES a filter, so a link can widen a
    // view as well as narrow it.
    if (expr === '') delete merged[field];
    else merged[field] = expr;
  }
  if (Object.keys(cmd.filters).length > 0 || cmd.options.clear !== undefined) {
    patch.filters = Object.keys(merged).length > 0 ? merged : undefined;
  }

  const sort = parseSort(cmd.options.sort, table);
  if (sort) {
    // `sortColumn`/`sortAsc` mirror the first key — the same shape data-table
    // writes, so an older reader still sees the primary sort.
    patch.sortBy = sort.length > 0 ? sort : undefined;
    patch.sortColumn = sort[0]?.field;
    patch.sortAsc = sort[0] ? sort[0].asc : undefined;
  }

  if (Object.keys(patch).length > 0) {
    const app = await getContext();
    await app.store.tables.patch(table.id, { ...patch, updatedAt: Date.now() } as Partial<Table>);
  }

  focusTableWindow(table.id);

  const search = cmd.options.search;
  if (search !== undefined) {
    document.dispatchEvent(new CustomEvent('easydb:table-search', { detail: { tableId: table.id, query: search } }));
  }
}

/**
 * Which table a `goto/…` acts on: the one it names, or — with no name — the one
 * the click came from.
 *
 * The target-less form is what lets a header, a KPI tile or a custom
 * visualization carry `#goto?Country==CH` and have it mean "this table". Naming
 * the table would tie the markup to one table; not naming it makes the same
 * block work wherever it is dropped. The exact mirror of {@link resolveView},
 * including the refusal: typed into the palette there is no table to be in, and
 * guessing one would silently rewrite a table the user is not looking at.
 */
async function resolveTable(cmd: Commandlet, ctx: CommandletContext): Promise<Table> {
  const name = (cmd.targets[0] ?? '').trim();
  if (name) return findTableByName(name);
  if (!ctx.tableId) {
    throw new CommandletError('"goto" with no name means the table you are in — this was not run from one, so name the table.');
  }
  const app = await getContext();
  const table = await app.store.tables.findOne(ctx.tableId);
  if (!table) throw new CommandletError('That table no longer exists.');
  return table;
}

/** `-Field` is descending; several keys are comma-separated, priority order. */
function parseSort(spec: string | undefined, table: Table): SortSpec[] | null {
  if (spec === undefined) return null;
  return spec
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (s.startsWith('-') ? { field: resolveField(table, s.slice(1)), asc: false } : { field: resolveField(table, s), asc: true }));
}

/**
 * Map what the commandlet wrote to a real column field, by field name OR label,
 * case-insensitively — the same rule `searchRowsByField` applies to a
 * `field:value` search term, so `Book` finds the column labelled "Book" even
 * though its field is `book`.
 *
 * An unknown column is refused rather than written. A filter on a field no
 * column has cannot be seen or cleared in the grid (there is no funnel for it)
 * and matches nothing, so writing one empties the table with no visible cause.
 */
function resolveField(table: Table, key: string): string {
  const byName = new Map<string, string>();
  for (const c of table.columns ?? []) {
    byName.set(c.field.toLowerCase(), c.field);
    if (c.label) byName.set(c.label.toLowerCase(), c.field);
  }
  const real = byName.get(key.trim().toLowerCase());
  if (!real) {
    const known = (table.columns ?? []).map((c) => c.field).join(', ');
    throw new CommandletError(`"${table.name}" has no column "${key}"${known ? ` — it has ${known}` : ''}.`);
  }
  return real;
}

// -- preview ------------------------------------------------------------------

/**
 * Show ONE cell in the preview window: `preview/<table>/<field>?<filters>`,
 * `preview/<table>/<key>`, or `preview/<table>/<field>/<key>`.
 *
 * Unlike every other verb this one does not navigate. Nothing is filtered,
 * focused or written — a link in a view can show a related record's notes
 * without disturbing what the reader is looking at, which is the whole reason to
 * have it rather than a `goto` that narrows a grid to one row.
 *
 * A filter matching SEVERAL rows shows the first and says so. Refusing would be
 * worse: the commonest way to get here is a link built from a value that is
 * nearly unique, and a window with the wrong-but-plausible record in it plus a
 * warning is more use than an error with nothing in it. The count is in the
 * warning so "nearly" can be seen to be wrong.
 */
async function runPreview(cmd: Commandlet, ctx: CommandletContext): Promise<void> {
  const app = await getContext();
  const table = await resolveTable(cmd, ctx);
  const plan = planPreview(table, cmd.targets.slice(1));
  if ('error' in plan) throw new CommandletError(plan.error);

  // The commandlet's own filters are resolved by name or label, exactly as
  // `goto` resolves them, so `?Title==Berlin` works against a column labelled
  // "Title" whose field is `title`. The key filter is already a real field name.
  const filters: Record<string, string> = { ...plan.keyFilter };
  for (const [key, expr] of Object.entries(cmd.filters)) {
    if (expr === '') continue;
    filters[resolveField(table, key)] = expr;
  }
  if (Object.keys(filters).length === 0) {
    throw new CommandletError('"preview" needs a key or a filter — without one there is no record to show.');
  }

  // Two rows are enough to know the answer is not unique; `total` still counts
  // every match, so the warning can say how many were passed over.
  const page = await readRows(app.store.rows(table.id), { columns: table.columns, filters, limit: 2 });
  const row = page.rows[0];
  if (!row) {
    throw new CommandletError(`No row in "${table.name}" matches ${describeFilters(filters)}.`);
  }

  const matches = page.total ?? page.rows.length;
  if (matches > 1) {
    app.api.ui.dialogs.toast(`${matches.toLocaleString()} rows match ${describeFilters(filters)} — showing the first.`, { kind: 'warning', title: 'Preview' });
  }

  // A RECORD PANE beside this table is a better answer than a one-field popup:
  // it shows the whole record, laid out, and it is editable in place. So a
  // `preview/notes/n-17` that named no field selects the row there instead.
  //
  // Only when no field was named — `preview/notes/Body/n-17` asked for one
  // column and keeps getting it — and only when something is actually following
  // this table's selection, which is what `current-row.ts` answers. Every link
  // already written therefore keeps working, and gets better where a pane exists.
  if (!namesAField(table, cmd.targets.slice(1))) {
    // Imported here, not at the top. `commandlet-run.ts` is pulled in by the
    // document-wide click handler that every cell link goes through, and one
    // verb's hand-off has no business enlarging that module's graph — see
    // `plugins/commandlets.ts`.
    const { currentRowWanted, setCurrentRow } = await import('../table/current-row.js');
    if (currentRowWanted(table.id)) {
      focusTableWindow(table.id);
      setCurrentRow(table.id, row.id);
      return;
    }
  }
  // **Not the record window.** A double-click opens one, and it was tempting to
  // make `preview/notes/n-17` agree — but a key-only preview is a designed
  // feature with its own rules for WHICH field is worth a window
  // (`commandlet-preview.ts`, pure and unit-tested), and every link already
  // written expects them. A record pane is different: it is already on screen,
  // and the reader asked for it.

  // Scripted columns are computed first, exactly as the grid and a view compute
  // them. The stored cell behind a script is empty, so previewing one without
  // this showed an empty window for a column plainly full of text on screen.
  const field = plan.field;
  const label = field.label || field.field;
  const frame = previewFrame();
  const draw = (r: Row) => {
    frame.innerHTML = '';
    frame.append(renderValue(evaluateRow(r, table.columns).data[field.field], field, app.registries.cellRenderers));
  };
  draw(row);

  // A `preview/…` window is often opened from a link, with the row it shows not on
  // screen at all — so the header names the record, and its button is the only way
  // to correct the value without hunting for the table first.
  //
  // The write is the grid's write: same validation, same `patch`, same refusal on
  // a read-only table or column. What it cannot check is `unique`, which needs
  // every row of the table; the next edit in the grid, or Validate, catches that.
  const key = keyColumnOf(table);
  const keyValue = key ? String(row.data[key.field] ?? '') : '';
  const writable = table.readonly !== true && field.readonly !== true;
  let current = row;

  openPreviewPopup(`${table.name} — ${label}`, frame, {
    label,
    note: keyValue || undefined,
    // The window shows one cell of a row that may not be on screen at all, so
    // the whole record is one click away. `table.readonly` is the right test
    // here — unlike `writable`, which also refuses a read-only COLUMN in a
    // table whose other fields are perfectly editable.
    recordLabel: table.readonly === true ? 'View record' : 'Edit record',
    onEditRecord: () => void openRecordForm(table.id, current.id),
    editLabel: writable ? 'Edit' : 'View source',
    onEdit: () => {
      // The STORED cell, not the computed one: saving a script's output over the
      // value the script reads would destroy it.
      const source = current.data[field.field];
      const text = source == null ? '' : String(source);
      if (!writable) {
        openHtmlEditor(`View ${label}`, text, () => undefined, { readonly: true });
        return;
      }
      openHtmlEditor(`Edit ${label}`, text, (next) => void saveField(current, field, next, table, draw, (r) => (current = r)));
    },
  });
}

/** The one write a preview window can make. Mirrors `data-table`'s `commitCell`. */
async function saveField(row: Row, field: ColumnSpec, next: string, table: Table, draw: (r: Row) => void, keep: (r: Row) => void): Promise<void> {
  const app = await getContext();
  const reason = validateValue(field, next, [], row.id, row);
  if (reason) {
    await app.api.ui.dialogs.alert(reason, `Cannot save ${field.label || field.field}`);
    return;
  }
  try {
    const saved = await app.store.rows(table.id).patch(row.id, {
      data: { ...row.data, [field.field]: next },
      updatedAt: Date.now(),
    });
    keep(saved);
    draw(saved);
  } catch (err) {
    // A remote row source can refuse the write — read-only table, expired token.
    await app.api.ui.dialogs.alert((err as Error)?.message ?? 'Could not save the change.', 'Save failed');
  }
}

/** `Title==Berlin + Year==2026`, for a message a person has to act on. */
function describeFilters(filters: Record<string, string>): string {
  return Object.entries(filters)
    .map(([field, expr]) => `${field}${expr}`)
    .join(' + ');
}

// -- edit ---------------------------------------------------------------------

/**
 * Open ONE record in the record form: `edit/<table>/<key>`,
 * `edit/<table>/<field>/<value>` or `edit/<table>?<filters>`.
 *
 * The same form as the table's + button, over an existing row — so a link, a
 * double-click and the palette all correct a record in one place instead of
 * hunting for its row in the grid. The form itself decides whether the record
 * may be WRITTEN, from `Table.readonly`: a commandlet cannot talk the app into
 * editing a read-only table.
 *
 * Several matches open the first and say so, exactly as `preview` does. The
 * commonest way here is a link built from a nearly-unique value, and a form with
 * a plausible record in it plus a count is more use than an error with nothing.
 */
async function runEdit(cmd: Commandlet, ctx: CommandletContext): Promise<void> {
  const app = await getContext();
  const table = await resolveTable(cmd, ctx);
  const filters = editFilters(table, cmd);

  const page = await readRows(app.store.rows(table.id), { columns: table.columns, filters, limit: 2 });
  const row = page.rows[0];
  if (!row) {
    throw new CommandletError(`No row in "${table.name}" matches ${describeFilters(filters)}.`);
  }
  const matches = page.total ?? page.rows.length;
  if (matches > 1) {
    app.api.ui.dialogs.toast(`${matches.toLocaleString()} rows match ${describeFilters(filters)} — editing the first.`, { kind: 'warning', title: 'Edit' });
  }

  await openRecordForm(table.id, row.id);
}

// -- record -------------------------------------------------------------------

/**
 * Open ONE record in the record WINDOW: `record/<table>/<rowId>`,
 * `record/<table>/<key>`, `record/<table>/<field>/<value>` or
 * `record/<table>?<filters>`.
 *
 * `edit`'s twin, and deliberately every shape `edit` takes — the difference is
 * where the record lands, not how it is named. A window rather than a modal form
 * is what lets two records sit open side by side, and it is what a row
 * double-click gives, so a link and the gesture now agree.
 *
 * **It is the one place a ROW ID addresses a row.** Everywhere else a record is
 * named by its key column, which is a convention (`keyColumnOf`) and not always
 * unique. `$_.rowId` / `row._.rowId` is the handle a template has on a record
 * that has no key of its own — an imported table with repeated values, a row the
 * user has not named yet — and without a verb that accepts it the metadata would
 * be visible and useless.
 *
 * The id is tried FIRST and falls through silently: a table whose key column
 * really does hold row-id-shaped values still resolves by key, because the
 * lookup that failed cost one `findOne`.
 */
async function runRecord(cmd: Commandlet, ctx: CommandletContext): Promise<void> {
  const app = await getContext();
  const table = await resolveTable(cmd, ctx);

  const wanted = rowIdTarget(cmd);
  let row: Row | undefined;
  if (wanted) {
    row = ((await app.store.rows(table.id).findOne(wanted)) as Row | null) ?? undefined;
  }
  if (!row) {
    const filters = editFilters(table, cmd);
    const page = await readRows(app.store.rows(table.id), { columns: table.columns, filters, limit: 2 });
    row = page.rows[0];
    if (!row) {
      throw new CommandletError(`No row in "${table.name}" matches ${describeFilters(filters)}.`);
    }
    const matches = page.total ?? page.rows.length;
    if (matches > 1) {
      app.api.ui.dialogs.toast(`${matches.toLocaleString()} rows match ${describeFilters(filters)} — opening the first.`, { kind: 'warning', title: 'Record' });
    }
  }

  // A record PANE beside this table is already showing records, so it is the
  // better answer than a new window — the same order a row double-click follows
  // (`plugins/record-popup.ts`), and the reason a link and the gesture agree.
  const { currentRowWanted, setCurrentRow } = await import('../table/current-row.js');
  if (currentRowWanted(table.id)) {
    focusTableWindow(table.id);
    setCurrentRow(table.id, row.id);
    return;
  }
  const { openRecordPopup } = await import('./record-popup.js');
  if (openRecordPopup(table.id, row.id)) return;
  // The Record visualization is switched off, so there is no window to open.
  // The FORM shows the same record and is always there — a verb that did
  // nothing because of a plugin toggle would be worse than one that lands
  // somewhere slightly different.
  await openRecordForm(table.id, row.id);
}

/**
 * The second target, where it could be a row id — `record/<table>/<id>`.
 *
 * Only the two-target form: `record/notes/Title/Berlin` named a field, so the
 * third segment is that field's value and not an id. Returns `''` when there is
 * nothing to try, which is also what a caller reads as "not by id".
 */
function rowIdTarget(cmd: Commandlet): string {
  if (cmd.targets.length !== 2) return '';
  return (cmd.targets[1] ?? '').trim();
}

/**
 * The record form, loaded on demand — the one effect this module does not route
 * to something already imported. A commandlet chain runs on the first-paint path
 * and the form is only ever wanted after the row has been found.
 */
async function openRecordForm(tableId: string, rowId: string): Promise<void> {
  const { openEditRecordDialog } = await import('../dialogs/new-record-dialog.js');
  await openEditRecordDialog(tableId, rowId);
}

/** The key/field targets plus the commandlet's own query, as one filter set. */
function editFilters(table: Table, cmd: Commandlet): Record<string, string> {
  const plan = planEditKey(table, cmd.targets.slice(1));
  if ('error' in plan) throw new CommandletError(plan.error);

  const filters: Record<string, string> = { ...plan.keyFilter };
  for (const [key, expr] of Object.entries(cmd.filters)) {
    if (expr === '') continue;
    filters[resolveField(table, key)] = expr;
  }
  if (Object.keys(filters).length === 0) {
    throw new CommandletError('"edit" needs a key or a filter — without one there is no record to open.');
  }
  return filters;
}

// -- search / view / cmd ------------------------------------------------------

function runSearch(cmd: Commandlet): void {
  const query = cmd.targets[0] ?? '';
  // The header box owns the global query, so tell it rather than broadcasting
  // behind its back — otherwise rows narrow while the box still looks empty.
  document.dispatchEvent(new CustomEvent('easydb:set-search', { detail: { query } }));
}

/**
 * Open a view, and narrow it the way `goto` narrows a table.
 *
 * Filters land on the view's `pillFilters`, NOT its own `filters`. Two reasons:
 * that layer shows as chips in the view's toolbar, so a link that narrows a view
 * can be seen and clicked off again, and the view's snapshotted `filters` are
 * part of how the user DEFINED the view — a navigation link must not quietly
 * rewrite that. `@clear` empties the chips; an empty value drops one field, as it
 * does for a table.
 */
async function runView(cmd: Commandlet, ctx: CommandletContext): Promise<void> {
  const inst = await resolveView(cmd, ctx);
  const table = await tableOfView(inst);

  const patch: Partial<ViewInstance> = {};
  const merged = cmd.options.clear === undefined ? { ...(inst.pillFilters ?? {}) } : {};
  for (const [key, expr] of Object.entries(cmd.filters)) {
    const field = resolveField(table, key);
    if (expr === '') delete merged[field];
    else merged[field] = expr;
  }
  if (Object.keys(cmd.filters).length > 0 || cmd.options.clear !== undefined) {
    patch.pillFilters = Object.keys(merged).length > 0 ? merged : undefined;
  }

  const sort = parseSort(cmd.options.sort, table);
  if (sort) {
    patch.sortBy = sort.length > 0 ? sort : undefined;
    patch.sortColumn = sort[0]?.field;
    patch.sortAsc = sort[0] ? sort[0].asc : undefined;
  }

  if (Object.keys(patch).length > 0) {
    const app = await getContext();
    await app.store.viewInstances.patch(inst.id, { ...patch, updatedAt: Date.now() } as Partial<ViewInstance>);
  }

  await revealViewWindow(inst.id);

  const search = cmd.options.search;
  if (search !== undefined) {
    // A view's search box is keyed by the INSTANCE id, not the table's — see
    // `view-window.ts`'s `onSearch` — so a view search never touches the table
    // window showing the same rows.
    document.dispatchEvent(new CustomEvent('easydb:table-search', { detail: { tableId: inst.id, query: search } }));
  }
}

/**
 * Which view a `view/…` acts on: the one it names, or — with no target — the one
 * the click came from. A target-less `view?…` typed into the palette has no such
 * context, and says so rather than guessing.
 */
async function resolveView(cmd: Commandlet, ctx: CommandletContext): Promise<ViewInstance> {
  const name = (cmd.targets[0] ?? '').trim();
  if (name) return findViewByName(name);
  if (!ctx.viewInstanceId) {
    throw new CommandletError('"view" with no name means the view you are in — this was not run from one, so name the view.');
  }
  const app = await getContext();
  const inst = await app.store.viewInstances.findOne(ctx.viewInstanceId);
  if (!inst) throw new CommandletError('That view no longer exists.');
  return inst;
}

/** The table a view shows, for resolving the field names in its query. */
async function tableOfView(inst: ViewInstance): Promise<Table> {
  const app = await getContext();
  const table = await app.store.tables.findOne(inst.tableId);
  if (!table) throw new CommandletError(`The view "${inst.name}" has no table.`);
  return table;
}

async function runCommandId(cmd: Commandlet): Promise<void> {
  const spec = await findCommandById(cmd.targets[0] ?? '');
  const app = await getContext();
  await spec.run(app.api);
}

async function findViewByName(rawName: string): Promise<ViewInstance> {
  const name = rawName.trim();
  const app = await getContext();
  const instances = (await app.store.viewInstances.find()).filter((v) => v.workspaceId === app.workspaceId);
  const match = instances.find((v) => v.name === name) ?? instances.find((v) => v.name.toLowerCase() === name.toLowerCase());
  if (!match) throw new CommandletError(`No view called "${name}".`);
  return match;
}

async function findCommandById(id: string): Promise<CommandSpec> {
  const app = await getContext();
  const spec = app.registries.commands.find((c) => c.id === id) ?? app.registries.commands.find((c) => c.id.toLowerCase() === id.toLowerCase());
  if (!spec) throw new CommandletError(`No command with id "${id}".`);
  return spec;
}

// -- lookup -------------------------------------------------------------------

/**
 * Tables are addressed BY NAME, like projections and view instances — a table
 * deleted and re-imported keeps its name but gets a new id, and a commandlet in
 * a bookmark has to survive that.
 */
async function findTableByName(name: string): Promise<Table> {
  const wanted = name.trim();
  if (!wanted) throw new CommandletError('No table name given.');
  const app = await getContext();
  const tables = (await app.store.tables.find()).filter((t) => t.workspaceId === app.workspaceId);
  const match = tables.find((t) => t.name === wanted) ?? tables.find((t) => t.name.toLowerCase() === wanted.toLowerCase());
  if (!match) throw new CommandletError(`No table called "${wanted}".`);
  return match;
}
